/**
 * Draw on a 2D canvas
 *
 * This implements a similar interface to the 3D renderer. It allows the
 * user to execute draws declaratively by using drawable objects.
 */
export class Draw2D {
    constructor(canvas) {
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "surface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "drawables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "imageCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.canvas = canvas;
        this.surface = this.canvas.getContext('2d');
        this.drawables = new Set();
        this.imageCache = new Map();
    }
    /**
     * Draw a rectangle
     *
     * @param rect
     */
    drawRect(rect) {
        if (this.surface === null)
            return;
        const { color, opacity, position, dimensions, fill, center, radius } = rect;
        this.surface.fillStyle = `#${color.getHexString()}`;
        this.surface.strokeStyle = `#${color.getHexString()}`;
        this.surface.globalAlpha = opacity;
        const topleft = position.clone();
        if (center) {
            topleft.sub(dimensions.clone().divideScalar(2));
        }
        if (!radius) {
            if (fill) {
                this.surface.fillRect(topleft.x, topleft.y, dimensions.x, dimensions.y);
            }
            else {
                this.surface.strokeRect(topleft.x, topleft.y, dimensions.x, dimensions.y);
            }
        }
        else {
            this.surface.beginPath();
            this.surface.moveTo(topleft.x, topleft.y);
            this.surface.arcTo(topleft.x + dimensions.x, topleft.y, topleft.x + dimensions.x, topleft.y + dimensions.y, radius);
            this.surface.arcTo(topleft.x + dimensions.x, topleft.y + dimensions.y, topleft.x, topleft.y + dimensions.y, radius);
            this.surface.arcTo(topleft.x, topleft.y + dimensions.y, topleft.x, topleft.y, radius);
            this.surface.arcTo(topleft.x, topleft.y, topleft.x + dimensions.x, topleft.y, radius);
            if (fill) {
                this.surface.fill();
            }
            else {
                this.surface.stroke();
            }
        }
    }
    /**
     * Draw a circle
     *
     * @param circle
     */
    drawCircle(circle) {
        if (this.surface === null)
            return;
        const { position, color, opacity, radius, fill } = circle;
        this.surface.fillStyle = `#${color.getHexString()}`;
        this.surface.strokeStyle = `#${color.getHexString()}`;
        this.surface.globalAlpha = opacity;
        this.surface.beginPath();
        this.surface.arc(position.x, position.y, radius, 0, 2 * Math.PI);
        if (fill) {
            this.surface.fill();
        }
        else {
            this.surface.stroke();
        }
    }
    /**
     * Draw a polygon
     *
     * @param polygon
     */
    drawPolygon(polygon) {
        if (this.surface === null)
            return;
        const { color, vertices, opacity, fill, border } = polygon;
        // Define the outline of the polygon
        this.surface.beginPath();
        for (let i = 0; i < vertices.length; i++) {
            const { x, y } = vertices[i];
            if (i === 0) {
                this.surface.moveTo(x, y);
            }
            else {
                this.surface.lineTo(x, y);
            }
        }
        this.surface.closePath();
        // Color it
        this.surface.globalAlpha = opacity;
        if (fill) {
            this.surface.fillStyle = `#${color.getHexString()}`;
            this.surface.fill();
        }
        else {
            this.surface.strokeStyle = `#${color.getHexString()}`;
            this.surface.lineWidth = border ?? 1;
            this.surface.stroke();
        }
    }
    /**
     * Draw a line
     *
     * @param line
     */
    drawLine(line) {
        if (this.surface === null)
            return;
        const { color, opacity, width, start, end } = line;
        this.surface.strokeStyle = `#${color.getHexString()}`;
        this.surface.globalAlpha = opacity;
        this.surface.lineWidth = width;
        this.surface.beginPath();
        this.surface.moveTo(start.x, start.y);
        this.surface.lineTo(end.x, end.y);
        this.surface.stroke();
    }
    /**
     * Draw some text
     *
     * @param text
     */
    drawText(text) {
        if (this.surface === null)
            return;
        const { position, size, color, opacity, string, font, center, fill } = text;
        this.surface.font = `${size}px ${font}`;
        this.surface.textBaseline = 'top';
        this.surface.fillStyle = `#${color.getHexString()}`;
        this.surface.strokeStyle = `#${color.getHexString()}`;
        this.surface.globalAlpha = opacity;
        if (center) {
            this.surface.textAlign = 'center';
            this.surface.textBaseline = 'middle';
        }
        else {
            this.surface.textAlign = 'left';
            this.surface.textBaseline = 'top';
        }
        if (fill) {
            this.surface.fillText(string, position.x, position.y);
        }
        else {
            this.surface.strokeText(string, position.x, position.y);
        }
    }
    /**
     * Draw an image from a URL
     *
     * @param image
     */
    drawImage(image) {
        // Image draw logic
        const { url, position, opacity, destinationSize } = image;
        const render = (obj) => {
            if (this.surface === null)
                return;
            this.surface.globalAlpha = opacity;
            this.surface.imageSmoothingEnabled = false;
            if (destinationSize) {
                this.surface.drawImage(obj, position.x, position.y, destinationSize.x, destinationSize.y);
            }
            else {
                this.surface.drawImage(obj, position.x, position.y);
            }
        };
        // Load the image file and cache it
        let obj = this.imageCache.get(url);
        if (obj === undefined) {
            obj = new Image();
            obj.src = url;
            this.imageCache.set(url, obj);
            obj.addEventListener('load', () => {
                if (obj === undefined)
                    return;
                render(obj);
            });
        }
        else {
            render(obj);
        }
    }
    /**
     * Handle each Drawable type
     *
     * @param drawable
     */
    draw(drawable) {
        switch (drawable.type) {
            case 'rect':
                this.drawRect(drawable);
                break;
            case 'circle':
                this.drawCircle(drawable);
                break;
            case 'polygon':
                this.drawPolygon(drawable);
                break;
            case 'line':
                this.drawLine(drawable);
                break;
            case 'text':
                this.drawText(drawable);
                break;
            case 'image':
                this.drawImage(drawable);
                break;
        }
    }
    /**
     * Get the HTML canvas
     *
     * @returns
     */
    getCanvas() {
        return this.canvas;
    }
    /**
     * Set the size of the canvas
     *
     * @param size
     */
    setSize(size) {
        this.canvas.width = size.x;
        this.canvas.height = size.y;
    }
    /**
     * Add a drawable
     *
     * @param drawable
     */
    addDrawable(drawable) {
        this.drawables.add(drawable);
    }
    /**
     * Remove a drawable, if it exists
     *
     * @param drawable
     */
    removeDrawable(drawable) {
        this.drawables.delete(drawable);
    }
    /**
     * Calculate the width in pixels of a string of text when
     * drawn with a given font and size
     *
     * @param text String of text
     * @param font Font type
     * @param size Size of the font
     * @returns
     */
    textWidth(text, font, size) {
        if (this.surface === null)
            return 0;
        this.surface.font = `${size}px ${font}`;
        return this.surface.measureText(text).width;
    }
    /**
     * Calculate the height in pixels of the drawn text given the size of a font
     *
     * @param size
     * @returns
     */
    textHeight(size) {
        return (size * 16) / 12; // 16px = 12pt
    }
    /**
     * Render a single frame of the canvas.
     *
     *
     * Do not call this within the application run time.
     * Run this in an infinite loop on initialization.
     */
    render() {
        this.surface?.clearRect(0, 0, this.canvas.width, this.canvas.height);
        // Sort drawables by z-index
        const sorted = [...this.drawables];
        sorted.sort((a, b) => {
            const za = a.zIndex ?? 0;
            const zb = b.zIndex ?? 0;
            return za - zb;
        });
        // Render the drawables
        for (const drawable of sorted) {
            this.draw(drawable);
        }
    }
}
